import React, { useMemo, useState } from 'react'
import WifiIcon from '@mui/icons-material/Wifi'
import LteMobiledataIcon from '@mui/icons-material/LteMobiledata'
import WindowIcon from '@mui/icons-material/Window'
import AndroidIcon from '@mui/icons-material/Android'
import AppleIcon from '@mui/icons-material/Apple'
import moment from 'moment'
import useMobile from '../hooks/useMobile'
import LoadingSpinner from './LoadingSpinner'
import MobilePaginationActions from './MobilePaginationActions'
import ProxyAgeActivity from './ProxyAgeActivity'

// Компонент тултипа (мобильно-дружественный)
const Tooltip = ({ children, title, onLongPress, isCheckingSpeed }) => {
    const [show, setShow] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [touchTimer, setTouchTimer] = useState(null)
    
    // Определяем, мобильное ли устройство
    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window)
        }
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])
    
    const handleTouchStart = (e) => {
        if (isMobile) {
            // Короткое касание - показываем тултип
            setShow(true)
            setTimeout(() => setShow(false), 3000)
            
            // Длительное зажатие - запускаем проверку скорости
            const timer = setTimeout(() => {
                if (onLongPress) {
                    console.log('Long press triggered!') // Отладка
                    onLongPress(e)
                }
            }, 600) // 600ms для длительного нажатия
            
            setTouchTimer(timer)
        }
    }
    
    const handleTouchEnd = (e) => {
        if (isMobile && touchTimer) {
            // Если отпустили до истечения 600ms, отменяем длительное нажатие
            clearTimeout(touchTimer)
            setTouchTimer(null)
        }
    }
    
    const handleTouchCancel = (e) => {
        if (isMobile && touchTimer) {
            clearTimeout(touchTimer)
            setTouchTimer(null)
        }
    }
    
    return (
        <div 
            style={{ 
                position: 'relative', 
                display: 'inline-block',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
                WebkitTouchCallout: 'none',
                WebkitTapHighlightColor: 'transparent'
            }}
            onMouseEnter={() => !isMobile && setShow(true)}
            onMouseLeave={() => !isMobile && setShow(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
        >
            {/* Показываем спиннер только если идет проверка скорости */}
            {isCheckingSpeed ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <LoadingSpinner size={22} />
                </div>
            ) : (
                children
            )}
            {show && (
                <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'var(--medium-gray)',
                    color: 'var(--text-light)',
                    padding: '8px 10px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--text-xs)',
                    whiteSpace: 'nowrap',
                    zIndex: 1000,
                    border: '1px solid var(--light-gray)',
                    boxShadow: 'var(--shadow-md)',
                    marginBottom: '5px',
                    // На мобильных устройствах делаем тултип более заметным
                    ...(isMobile && {
                        backgroundColor: 'var(--dark-gray)',
                        border: '2px solid var(--primary-orange)',
                        fontSize: 'var(--text-sm)',
                        padding: '10px 12px'
                    })
                }}>
                    {title}
                </div>
            )}
        </div>
    )
}

const MobileProxyTable = ({ 
  proxies, 
  selectedRow, 
  onRowClick, 
  onBuyClick, 
  onSpeedCheck, 
  isCheckingSpeed, 
  proxySpeed,
  speedCheckTimes,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loadingButton
}) => {
  const { isMobile } = useMobile()

  // Мемоизируем список прокси для лучшей производительности
  const proxyList = useMemo(() => {
    return proxies?.proxies || []
  }, [proxies?.proxies])

  return (
    <div style={{ padding: 'var(--spacing-sm)' }}>
      {proxyList.map((row) => (
        <div
          key={row.proxyid}
          onClick={() => onRowClick(row)}
          style={{
            background: 'var(--medium-gray)',
            border: '1px solid var(--light-gray)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-sm)',
            cursor: 'pointer',
            borderLeft: selectedRow?.proxyid === row.proxyid ? '4px solid var(--primary-orange)' : '4px solid transparent',
            transition: 'var(--transition-normal)'
          }}
        >
          {/* Заголовок карточки */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 'var(--spacing-md)'
          }}>
            <div>
              <div style={{
                fontSize: 'var(--text-lg)',
                fontWeight: '600',
                color: 'var(--text-light)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                {row?.ip.replace(/\_/g, '*')}
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)'
              }}>
                {row?.geo?.city}, {row?.geo?.reg} • {row?.geo?.zip}
              </div>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              flexShrink: 0
            }}>
              {row?.os === 'windows' ? (
                <WindowIcon style={{ fontSize: 18, fill: 'url(#goldGradient)' }} />
              ) : row?.os === 'android' ? (
                <AndroidIcon style={{ fontSize: 18, fill: 'url(#goldGradient)' }} />
              ) : row?.os === 'macos' ? (
                <AppleIcon style={{ fontSize: 18, fill: 'url(#goldGradient)' }} />
              ) : null}
              {row?.network === 'wifi' ? (
                <WifiIcon style={{ fontSize: 18, fill: 'url(#goldGradient)' }} />
              ) : (
                <LteMobiledataIcon style={{ fontSize: 18, fill: 'url(#goldGradient)' }} />
              )}
            </div>
          </div>

          {/* Детали прокси */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-md)'
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                ISP
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                minWidth: 0
              }}>
                <span style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%'
                }}>{row?.geo?.isp}</span>
              </div>
            </div>
            
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                Users
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)'
              }}>
                {row?.users > 0 ? row?.users + '/4' : row?.users}
              </div>
            </div>
            
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                Age
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)'
              }}>
                <ProxyAgeActivity listedDays={row?.listedDays} uptime={row?.uptime} mode="age" />
              </div>
            </div>
            
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                Activity
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)'
              }}>
                <ProxyAgeActivity listedDays={row?.listedDays} uptime={row?.uptime} mode="activity" />
              </div>
            </div>
          </div>

          {/* Кнопки действий */}
          <div style={{
            display: 'flex',
            gap: 'var(--spacing-sm)',
            marginTop: 'var(--spacing-sm)'
          }}>
            {/* Результат скорости с тултипом */}
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px 12px',
              background: 'var(--dark-gray)',
              border: '1px solid var(--light-gray)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-light)',
              fontSize: 'var(--text-sm)',
              fontWeight: '500'
            }}>
              <Tooltip 
                title={speedCheckTimes?.[row?.proxyid] ? moment(speedCheckTimes[row?.proxyid]).fromNow() : 'Hold to test speed'}
                onLongPress={() => {
                  onSpeedCheck(row?.proxyid)
                }}
                isCheckingSpeed={isCheckingSpeed[row?.proxyid] === true}
              >
                <span>{proxySpeed[row?.proxyid] != null ? `${proxySpeed[row?.proxyid]} Mbps` : 'Check speed'}</span>
              </Tooltip>
            </div>
            
            <button 
              className="btn btn-buy btn-small"
              onClick={(e) => {
                e.stopPropagation()
                onBuyClick(row)
              }}
              style={{ flex: 1 }}
            >
              Buy
            </button>
          </div>
        </div>
      ))}
      
      {/* Мобильная пагинация */}
      {proxyList.length > 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-sm)',
          padding: 'var(--spacing-md)',
          background: 'var(--medium-gray)',
          border: '1px solid var(--light-gray)',
          borderRadius: 'var(--radius-lg)',
          marginTop: 'var(--spacing-md)'
        }}>
          {/* Строка с количеством строк на странице */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: 'var(--spacing-sm)' 
          }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
              Rows per page:
            </span>
            <select 
              value={rowsPerPage}
              onChange={onRowsPerPageChange}
              style={{
                background: 'var(--dark-gray)',
                border: '1px solid var(--light-gray)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-light)',
                padding: '6px 8px',
                fontSize: 'var(--text-sm)',
                minWidth: '60px'
              }}
            >
              {[10, 20, 30, 40, 50].map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          
          {/* Строка с навигацией */}
          <MobilePaginationActions
            page={page}
            rowsPerPage={rowsPerPage}
            count={proxies?.total || 0}
            onPageChange={onPageChange}
            loadingButton={loadingButton}
            pageAction
          />
        </div>
      )}
    </div>
  )
}

export default MobileProxyTable
